import React, { Component } from 'react'
import { Container, Row, Col } from 'reactstrap'
import ReturnsList from "../CA/ReturnsList"
import '../CA/CarsList.css'
import RentalsListHeader from "../Rentals/RentalListNew/RentalsListHeader"
import ListingTabs from "../Material/ListingTabs"
import { buildPresets } from "../Filters/presetFilters"
import ReportsPanel from "../Reports/ReportsPanel"
import { ROUTES } from "../Layout/routes"
import { withPathTab } from "../Functions/usePathTab"

import withApolloClient from '../withApolloClient'

// Each tab's default filters, expressed as Advanced Filters presets -- the same
// FilterInput[] shape the query already sends for user filters, so the popover,
// the chips and Reset all read one source of truth. See IncidentsListContainer
// for the pattern this follows.
//
// Every one of them is locked (editable: false). These are not defaults the user
// is meant to adjust -- they are what each tab IS: Awaiting To Closeout is stage
// 3, Incomplete Returns is stage 5 restricted to Returns and Switches, Lost is
// return_type Lost. Each renders as a chip with a lock icon and a disabled row
// in the popover, so the user can see the scoping without being able to change
// or drop it. Users narrow further with their own filters instead.
//
// fieldName is "stage", not "return_stage": CarReturn has both fields, but the
// legacy returnStage resolver argument has always filtered stage__in (see
// resolve_all_returns in cars/schema.py) -- return_stage instead records the
// *car's* stage on return day. Using it here would silently filter the wrong
// column.
//
// Enum values are strings ("3", not 3): enum options are advertised as strings
// (FilterFieldOptionType.value is a graphene.String()), and that is what the
// chip label and the popover select look values up by. See sameOptionValue in
// Filters/AdvancedFilters.js -- the backend coerces either form, so a number
// would still filter correctly, it would just render the chip as a bare "3".
export const RETURN_LISTING_TABS = [
    {
        id: "awaitingToCloseout", name: "Awaiting To Closeout", iconClassName: "fa fa-reply",
        orderBy: ["-agreement__driver__in_office", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: ["3"], editable: false },
        ],
    },
    {
        id: "incompleteReturns", name: "Incomplete Returns", iconClassName: "fa fa-hourglass-half",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: ["5"], editable: false },
            { fieldName: "return_type", filterType: "in", filterValues: ["Return", "Switch"], editable: false },
        ],
    },
    {
        id: "lost", name: "Lost", iconClassName: "fa fa-times",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "return_type", filterType: "equal", filterValues: "Lost", editable: false },
        ],
    },
    {
        id: "all", name: "All Returns", iconClassName: "fa fa-calendar-o",
        orderBy: ["-dateAdded", "-agreement__driver__in_office"],
        getPresetFilters: () => [],
    },
    {
        id: "reports", name: "Activity Log", iconClassName: "fa fa-history", floatRight: true, iconOnly: true,
        orderBy: ["-dateAdded", "-agreement__driver__in_office"],
        getPresetFilters: () => [],
    },
]

export const getPresetsForTab = (tabId) =>
    buildPresets(RETURN_LISTING_TABS.find(tab => tab.id === tabId)?.getPresetFilters() || [])

// allReturns still takes the legacy scalar arguments (returnStage, returnType,
// returnTypeIn) alongside the generic `filters` argument -- see
// resolve_all_returns in cars/schema.py. ReturnsList.js now combines this
// container's `presets` prop with the URL's advancedFilters into `filters`
// (see combineFilters in Filters/presetFilters and ReturnsList.js), so the
// active tab's presets reach the query that way. No frontend caller sends the
// scalar args any more -- Available Cars / Scheduled Returns
// (frontend/src/CA/AvailableCars) was the last one, and it now expresses its
// returnStage/agreementStage defaults as presets too.
class CarReturnListContainer extends Component {
    constructor(props) {
        super(props)
        // presets and defaultPresets start out equal -- setPresets only ever
        // updates presets, so defaultPresets stays the active tab's declared
        // set and is what Reset returns to.
        const initialTab = RETURN_LISTING_TABS.find(tab => tab.id === props.activeTabId)
        const initialPresets = getPresetsForTab(initialTab.id)
        this.state = {
            searchTerm: "",
            selectedIds: [],
            filterValues: {
                orderBy: initialTab.orderBy,
            },
            presets: initialPresets,
            defaultPresets: initialPresets,
            refetchReservations: false,
        }
    }
    updateOrder = (string) => {
        let order = [...this.state.filterValues.orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string, order.find(item => item.includes("-agreement__driver__in_office"))]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        this.setState({ filterValues: { ...this.state.filterValues, orderBy: order } })
    }
    updateSelection = (selection) => {
        if (Array.isArray(selection)) {
            this.setState({ selectedIds: selection })
        } else {
            if (this.state.selectedIds.includes(selection))
                this.setState({ selectedIds: this.state.selectedIds.filter(item => item != selection) })
            else
                this.setState({ selectedIds: [...this.state.selectedIds, selection] })
        }
    }
    setPresets = (presets) => this.setState({ presets })

    applyTab = (tabId) => {
        const tab = RETURN_LISTING_TABS.find(item => item.id === tabId)
        const presets = getPresetsForTab(tab.id)
        this.setState({ filterValues: { orderBy: tab.orderBy }, presets, defaultPresets: presets })
    }

    componentDidUpdate(prevProps) {
        // The tab follows the URL: tab clicks, sidebar links and Back/Forward all land here.
        if (prevProps.activeTabId !== this.props.activeTabId) this.applyTab(this.props.activeTabId)
    }

    render() {
        const activeTab = RETURN_LISTING_TABS.find(tab => tab.id === this.props.activeTabId)
        return (
            <Container fluid className="CarListContainer">
                <ListingTabs
                    tabs={RETURN_LISTING_TABS}
                    activeTab={this.props.activeTabId}
                    onChange={this.props.changeTab}
                />
                {this.props.activeTabId !== "reports" && <RentalsListHeader
                    activeTab={activeTab.name}
                    filterValues={this.state.filterValues}
                    setFilterValues={(filterValues) => this.setState({ filterValues })}
                    location={this.props.location} history={this.props.navigate}
                    presets={this.state.presets}
                    setPresets={this.setPresets}
                    defaultPresets={this.state.defaultPresets}
                    selectedDrivers={this.state.selectedIds}
                    resetSelectedDrivers={() => this.setState({ selectedIds: [] })}
                />}

                <Row className="pb-2">
                    <Col sm="12">
                        {this.props.activeTabId === "reports" ?
                            <ReportsPanel reportId="car_returns" />
                            :
                            <ReturnsList
                                currentUser={this.props.currentUser}
                                searchTerm={this.state.filterValues && this.state.filterValues.searchTerm ? this.state.filterValues.searchTerm : null}
                                selectedDrivers={this.state.selectedIds}
                                updateDriversSelection={this.updateSelection}
                                orderBy={this.state.filterValues && this.state.filterValues.orderBy ? this.state.filterValues.orderBy : null}
                                presets={this.state.presets}
                                updateOrder={this.updateOrder}
                                activeTab={activeTab.name}
                            />
                        }
                    </Col>
                </Row>
            </Container>
        )
    }
}


export default withPathTab(ROUTES.carReturns)(withApolloClient(CarReturnListContainer))
